import React, { useState, useEffect } from "react";
import axios from "axios";
import SecureLS from "secure-ls";
import Swal from "sweetalert2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";

const ls = new SecureLS({ encodingType: "aes" });

const VoucherRedemptionModal = ({ open, onClose, reopenModal }) => {
  const user = ls.get("user") || {};
  const token = ls.get("authToken") || "";
  const posId = user.posId;
  const userId = user.userId;
  const userRoleId = user.roleId;

  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode("");
      setVoucher(null);
      setLoading(false);
    }
  }, [open]);

  const doClose = () => {
    setCode("");
    setVoucher(null);
    onClose();
  };

  const showError = async (msg) => {
    doClose();
    await Swal.fire({ icon: "error", text: msg });
    reopenModal();
  };

  const handleLookup = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setVoucher(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/apply-voucher2`,
        { voucherCode: code.trim(), subtotal: 0, posId, userId, userRoleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!data.voucher || !data.voucher.products?.length) {
        return showError(data.message || "Invalid voucher.");
      }
      setVoucher(data.voucher);
    } catch (err) {
      return showError(err.response?.data?.message || "Lookup failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!voucher) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/free-meal`,
        {
          posId,
          voucherCode: code.trim(),
          salesType: "Dine-In",
          tenderType: "Cash",
          userId,
          userRoleId,
          menuItems: voucher.products.map((p) => ({
            menuItemId: p.menuItemId,
            quantity: p.quantity || 1,
            name: p.menuName || "",
          })),
        },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = URL.createObjectURL(
        new Blob([data], { type: "application/pdf" })
      );
      doClose();
      window.open(url, "_blank", "noopener,noreferrer");
      await Swal.fire({ icon: "success", title: "Free meal redeemed!" });
      setCode("");
      setVoucher(null);
    } catch (err) {
      let msg = "Redemption failed.";
      if (err.response?.data?.type?.includes("json")) {
        try {
          const text = await err.response.data.text();
          msg = JSON.parse(text).message || msg;
        } catch {}
      }
      return showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const dialogClose = (_, reason) => {
    if (voucher) return;                     
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      doClose();
    } else {
      doClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={dialogClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={Boolean(voucher)}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Redeem Free Meal
        <IconButton
          onClick={() => !voucher && doClose()}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          label="Voucher Code"
          fullWidth
          size="small"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLookup()}
          autoFocus
        />

        {loading && (
          <CircularProgress
            size={24}
            sx={{ mt: 2, display: "block", mx: "auto" }}
          />
        )}

        {voucher && (
          <>
            <Typography sx={{ mt: 2, mb: 1 }} fontWeight={600}>
              Included Items
            </Typography>
            <List dense>
              {voucher.products.map((p) => (
                <ListItem key={p.menuItemId}>
                  <ListItemText
                    primary={`${p.menuName || "Item"} ×${p.quantity || 1}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(voucher)}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          disabled={!code.trim() || loading || Boolean(voucher)}
          onClick={handleLookup}
        >
          Lookup
        </Button>
        <Button
          variant="contained"
          disabled={!voucher || loading}
          onClick={handleRedeem}
        >
          Redeem
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoucherRedemptionModal;
